import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

/* ════════════════════════════════════════════════════════════
   CATALOGS — the ONLY place a tier name or cadence name is ever
   written as a string. Every other table below references these
   by id. If you need a tier's display label, look it up here —
   never type "premium" or "titanium" anywhere else in this file.
   ════════════════════════════════════════════════════════════ */
/* postPriceMultiplier scales the tier's base pricePerPost for that
   platform — a YouTube long-form video costs real production/edit
   time vs a Facebook text post, so it can't share one flat rate.
   1.0 = baseline (Facebook). Everything else priced relative to it. */
export const SOCIAL_MEDIA_TYPES = [
  { id: 1, name: "facebook",  label: "Facebook",  postPriceMultiplier: 1.0 },
  { id: 2, name: "instagram", label: "Instagram", postPriceMultiplier: 1.15 },
  { id: 3, name: "tiktok",    label: "TikTok",    postPriceMultiplier: 1.4 },
  { id: 4, name: "youtube",   label: "YouTube",   postPriceMultiplier: 2.5 },  // video edit/render is the expensive one
  { id: 5, name: "x",         label: "X",         postPriceMultiplier: 0.75 }, // shortest, cheapest to produce
  { id: 6, name: "linkedin",  label: "LinkedIn",  postPriceMultiplier: 1.2 },
  { id: 7, name: "snapchat",  label: "Snapchat",  postPriceMultiplier: 1.3 },
] as const;
export type SocialMediaType = typeof SOCIAL_MEDIA_TYPES[number]["name"];
export const socialMediaTypeById = (id: number) => SOCIAL_MEDIA_TYPES.find(s => s.id === id)!;


export const SUBSCRIPTION_TIERS = [
  { id: 0, name: "starter",  label: "Starter" },
  { id: 1, name: "basic",    label: "Basic" },
  { id: 2, name: "premium",  label: "Premium" },
  { id: 3, name: "titanium", label: "Titanium" },
  { id: 4, name: "elite",    label: "Elite" },
] as const;
export type SubscriptionTier = typeof SUBSCRIPTION_TIERS[number]["name"];
/* ebuosystem has no "starter" tier — starter is where the metered
   products' entry price lives. */
export type EbuosTier = Exclude<SubscriptionTier, "starter">;

export const tierById = (id: number) => SUBSCRIPTION_TIERS.find((t) => t.id === id)!;
export const tierByName = (name: SubscriptionTier) => SUBSCRIPTION_TIERS.find((t) => t.name === name)!;

export const EBUOS_TIER_ORDER = SUBSCRIPTION_TIERS
  .filter((t) => t.name !== "starter")
  .map((t) => t.name) as EbuosTier[];
export const ALL_TIER_ORDER = SUBSCRIPTION_TIERS.map((t) => t.name) as SubscriptionTier[];
/** @deprecated use ALL_TIER_ORDER */
export const AUTOMATION_TIER_ORDER = ALL_TIER_ORDER;

/* `days` = how many days that cadence covers (used by Digital
   Creator's content-quota math). `discountVsDaily` = how much
   cheaper a post is on this cadence vs paying the pure daily rate.
   `factor`/`monthsCovered` are the existing multipliers, and are
   what every *monthly-rated* product (ebuosystem, automation,
   customer agent, security, website hosting) bills on:
   monthlyTotal * factor. Yearly factor 10 vs 12 months covered =
   two months free; daily factor (1/30)*1.25 = a 25% convenience
   premium for no commitment. */
export const TIER_TYPES = [
  { id: 99,  name: "daily",   label: "Daily",   short: "/day",   days: 1,   discountVsDaily: 0,    factor: (1 / 30) * 1.25,    monthsCovered: 1 / 30 },
  { id: 100, name: "weekly",  label: "Weekly",  short: "/week",  days: 7,   discountVsDaily: 0.05, factor: (1 / 4.345) * 1.15, monthsCovered: 1 / 4.345 },
  { id: 101, name: "monthly", label: "Monthly", short: "/month", days: 30,  discountVsDaily: 0.15, factor: 1,                  monthsCovered: 1 },
  { id: 102, name: "yearly",  label: "Yearly",  short: "/year",  days: 365, discountVsDaily: 0.30, factor: 10,                 monthsCovered: 12 },
] as const;
export type TierType = typeof TIER_TYPES[number]["name"];
export const TIER_TYPE_ORDER = TIER_TYPES.map((t) => t.name) as TierType[];
export const tierTypeByName = (name: TierType) => TIER_TYPES.find((t) => t.name === name)!;

export type ProductType =
  | "ebuosystem"
  | "digitalCreator"
  | "automation"
  | "customerAgent"
  | "security"
  | "website";

export type DiscountFlags = {
  is5?: boolean; is10?: boolean; is25?: boolean; is50?: boolean; is80?: boolean;
};

const r2 = (n: number) => +(n.toFixed(2));
const r4 = (n: number) => +(n.toFixed(4));

export const discountPercentFrom = (d: DiscountFlags = {}): number => {
  if (d.is80) return 0.8;
  if (d.is50) return 0.5;
  if (d.is25) return 0.25;
  if (d.is10) return 0.1;
  if (d.is5)  return 0.05;
  return 0;
};

/* Not a price — a conversation. Shared contact-sales funnel for
   every product. */
export const ENTERPRISE = {
  name: "Enterprise",
  price: null as null,
  cta: "Contact sales",
  contactUrl: "https://ebuos.com/contact-sales",
  blurb: "Custom limits, SSO, a signed SLA, and a named contact.",
  includes: [
    "Every section, unlimited use",
    "Negotiated rates",
    "SSO / SAML and audit export",
    "99.9% SLA with penalties",
    "Dedicated support channel",
  ],
};

/* Shared trigger, extended (optionally) with a volume cap. Each
   metered product passes its own unit label so the reason reads
   like a human wrote it: "Over 1,000,000 conversations/month". */
export const needsSalesCall = (
  customTerms = false,
  volume?: number,
  volumeThreshold?: number,
  unitLabel = "customers/month",
): string[] => {
  const reasons: string[] = [];
  if (customTerms) reasons.push("Custom terms, SSO, or a signed SLA");
  if (volume != null && volumeThreshold != null && volume > volumeThreshold) {
    reasons.push(`Over ${volumeThreshold.toLocaleString()} ${unitLabel}`);
  }
  return reasons;
};

/* ════════════════════════════════════════════════════════════
   SHARED MONEY PLUMBING
   Every product ends the same way: subtotal → promo → credits →
   VAT → total. And every monthly-rated product converts a monthly
   subtotal onto the chosen cadence the same way. Written once.
   ════════════════════════════════════════════════════════════ */

export type SettleArgs = {
  discountApplied?: DiscountFlags;
  creditsAvailable?: number;
  vatRate?: number;
};

const settle = (subtotal: number, { discountApplied = {}, creditsAvailable = 0, vatRate = 0 }: SettleArgs) => {
  const discountPercent = discountPercentFrom(discountApplied);
  const promoDiscount = r2(subtotal * discountPercent);
  const afterDiscount = r2(subtotal - promoDiscount);
  const creditsUsed = r2(Math.min(Math.max(0, creditsAvailable), afterDiscount));
  const taxable = r2(afterDiscount - creditsUsed);
  const vat = r2(taxable * vatRate);
  const totalCharged = r2(taxable + vat);
  return {
    discountPercent,
    promoDiscount,
    creditsAvailable,
    creditsUsed,
    vatRate,
    vat,
    totalCharged: totalCharged as number | null,
  };
};

const applyCadence = (monthlyTotal: number, cadence: TierType) => {
  const cad = tierTypeByName(cadence);
  const recurringCharge = r2(monthlyTotal * cad.factor);
  const monthEquivalent = r2(recurringCharge / cad.monthsCovered);
  const savingsVsMonthly = monthlyTotal > 0
    ? r2(Math.max(0, (monthlyTotal - monthEquivalent) / monthlyTotal) * 100)
    : 0;
  return {
    cad,
    cadence,
    cadenceLabel: cad.label,
    cadenceShort: cad.short,
    recurringCharge,
    monthEquivalent,
    savingsVsMonthly,
  };
};

/* ── Scale bands ───────────────────────────────────────────────
   Same idea as calculateGuestServiceFee in shortstays: the more
   volume you push, the cheaper each unit gets. Difference is these
   are GRADUATED (tax-bracket style), not cliff-based — moving from
   999 to 1,001 conversations can never make your bill jump, it just
   prices the 2 extra units in the next band down.

   `freeUnits` is the tier's included allowance: those units walk
   the bands as normal but are charged at zero, so the allowance is
   always worth the most expensive units first.
   ─────────────────────────────────────────────────────────────── */

export type ScaleBand = { readonly upTo: number; readonly unitPrice: number; readonly label: string };

export type ScaleLine = {
  label: string;
  units: number;
  freeUnits: number;
  unitPrice: number;
  charge: number;
};

export type ScaleQuote = {
  units: number;
  freeUnits: number;
  billableUnits: number;
  total: number;
  effectiveUnitRate: number;
  breakdown: ScaleLine[];
};

export const priceScale = (units: number, bands: readonly ScaleBand[], freeUnits = 0): ScaleQuote => {
  const total = Math.max(0, Math.floor(units));
  const free = Math.max(0, Math.floor(freeUnits));
  const breakdown: ScaleLine[] = [];
  let lower = 0;
  let charge = 0;
  let billable = 0;

  const consume = (label: string, upper: number, unitPrice: number) => {
    const inBand = upper - lower;
    if (inBand <= 0) return;
    const freeHere = Math.max(0, Math.min(inBand, free - lower));
    const charged = inBand - freeHere;
    charge += charged * unitPrice;
    billable += charged;
    breakdown.push({ label, units: inBand, freeUnits: freeHere, unitPrice, charge: r2(charged * unitPrice) });
  };

  for (const band of bands) {
    if (lower >= total) break;
    consume(band.label, Math.min(total, band.upTo), band.unitPrice);
    lower = band.upTo;
  }

  // Past the top band we keep charging at the top band's rate. In
  // practice the product's enterprise threshold fires first.
  if (bands.length && total > lower) {
    const last = bands[bands.length - 1];
    consume(`Over ${lower.toLocaleString()}`, total, last.unitPrice);
  }

  return {
    units: total,
    freeUnits: Math.min(free, total),
    billableUnits: billable,
    total: r2(charge),
    effectiveUnitRate: total > 0 ? r4(charge / total) : 0,
    breakdown,
  };
};

/* Lowest tier in a pricing table that satisfies a predicate —
   used by every metered product to work out the floor tier a
   merchant's scale forces them onto. Falls through to the top tier. */
const lowestTierWhere = <T extends { tierId: number }>(
  rows: readonly T[],
  ok: (row: T) => boolean,
): SubscriptionTier => {
  const sorted = [...rows].sort((a, b) => a.tierId - b.tierId);
  const match = sorted.find(ok);
  return tierById(match ? match.tierId : sorted[sorted.length - 1].tierId).name as SubscriptionTier;
};

const liftTier = (chosen: SubscriptionTier, floor: SubscriptionTier): SubscriptionTier =>
  tierByName(floor).id > tierByName(chosen).id ? floor : chosen;

/* Gate a list of module/feature ids by tier. Shared by automation
   and security — they still let a merchant pick specific modules,
   so they still need "you asked for these, your tier only unlocks
   these". Ebuosystem's 17 sections no longer work this way — see
   the note above computeCategorySubscription. */
const splitByTier = <T extends { id: number; minTierId: number }>(
  catalog: readonly T[],
  requestedIds: number[],
  tier: SubscriptionTier,
) => {
  const tierId = tierByName(tier).id;
  const known = Array.from(new Set(requestedIds)).filter((id) => catalog.some((m) => m.id === id));
  const active = known.filter((id) => catalog.find((m) => m.id === id)!.minTierId <= tierId);
  const blocked = known.filter((id) => !active.includes(id));
  return { known, active, blocked };
};

const minTierForModules = <T extends { id: number; minTierId: number }>(
  catalog: readonly T[],
  requestedIds: number[],
): SubscriptionTier => {
  const needed = requestedIds.reduce((max, id) => {
    const row = catalog.find((m) => m.id === id);
    return row && row.minTierId > max ? row.minTierId : max;
  }, 0);
  return tierById(needed).name as SubscriptionTier;
};

/* ════════════════════════════════════════════════════════════
   EBUOSYSTEM — flat tier pricing. A merchant picks a tier and
   gets every feature category up to it. Every row below carries
   a tierId, never a tier name.
   ════════════════════════════════════════════════════════════ */

export const EBUOS_FEATURES = [
  { id: 1,  name: "Legal Business" },
  { id: 2,  name: "Domain" },
  { id: 3,  name: "Website" },
  { id: 4,  name: "Brand" },
  { id: 5,  name: "Products" },
  { id: 6,  name: "Payments" },
  { id: 7,  name: "Inventory & Fulfilment" },
  { id: 8,  name: "Marketing" },
  { id: 9,  name: "Sales" },
  { id: 10, name: "Customer Service" },
  { id: 11, name: "Bookkeeping" },
  { id: 12, name: "Employees" },
  { id: 13, name: "Business Management" },
  { id: 14, name: "Analytics" },
  { id: 15, name: "AI & Automation" },
  { id: 16, name: "Security" },
  { id: 17, name: "AI CEO System" },
] as const;
export const TOTAL_FEATURES = EBUOS_FEATURES.length;

/* NOTE: the explicit <number> is what fixes
   "Argument of type 'number' is not assignable to parameter of type
   '1 | 2 | 3 | ... | 17'". Without it, `as const` makes this a
   Set<1|2|...|17> and `.has(someNumber)` is a type error, because
   Set<T>.has only accepts T. We genuinely want to ask "is this
   arbitrary number one of ours?", so the set must be Set<number>. */
const FEATURE_ID_SET = new Set<number>(EBUOS_FEATURES.map((f) => f.id));
export const isFeatureId = (id: number): boolean => FEATURE_ID_SET.has(id);
export const featureName = (id: number): string | undefined =>
  EBUOS_FEATURES.find((f) => f.id === id)?.name;

/* ════════════════════════════════════════════════════════════
   PER-CATEGORY PRICING — every one of the 17 platform sections
   is its own product with its own tier ladder, because they cost
   wildly different amounts to actually build and run. Legal
   Business is compliance/document generation across jurisdictions;
   Domain is thin-margin reselling of registrar/SSL/DNS capacity.
   Charging them the same per-feature rate (the old shared ladder)
   was never realistic.

   Three sections are NOT priced here, because they already grew
   into their own full products with their own metering above:
     3  Website          → WEBSITE_TYPES / computeWebsiteSubscription
     15 AI & Automation   → DIGITAL_CREATOR_PRICING (1501),
                             CUSTOMER_AGENT_PRICING (1509),
                             AUTOMATION_PRICING (the rest, 1502-1516)
     16 Security          → SECURITY_PRICING

   The other 14 sections share one shape: a tier buys the section's
   base platform fee AND a fixed slice of that section's subcategory
   features — Basic gets a taste, Elite gets everything. THE TIER IS
   THE FEATURE SET. There is no picker: a merchant does not choose
   which subcategories they get, the tier decides that for them, the
   same way a Netflix plan decides your resolution rather than
   letting you tick boxes. `buildLadder` only removes the repetition
   of computing "how many of this category's subcategories does each
   tier unlock" — every price is hand-set per category, not derived.
   ════════════════════════════════════════════════════════════ */

export type CategoryTierRow = {
  readonly tierId: number;
  readonly monthlyBase: number;
  readonly includedSubcategories: number;
};
export type CategoryPricingTable = readonly CategoryTierRow[];

/* Starter unlocks a small taste, Elite unlocks everything (fraction
   1 → every subcategory the section has). */
const CATEGORY_TIER_FRACTIONS = [0.15, 0.35, 0.65, 0.9, 1] as const;

const buildLadder = (
  totalSubcategories: number,
  monthlyBases: readonly [number, number, number, number, number],
): CategoryPricingTable =>
  monthlyBases.map((monthlyBase, i) => ({
    tierId: i,
    monthlyBase,
    includedSubcategories: Math.min(totalSubcategories, Math.max(1, Math.round(totalSubcategories * CATEGORY_TIER_FRACTIONS[i]))),
  }));

/* [starter, basic, premium, titanium, elite]. Subcategory counts
   match appSubcategories.ts. */
export const LEGAL_BUSINESS_PRICING       = buildLadder(17, [29, 69, 149, 299, 599]);   // compliance + document generation, jurisdiction logic
export const DOMAIN_PRICING               = buildLadder(6,  [5,  12, 25,  45,  89]);    // thin-margin resale of registrar/SSL/DNS
export const BRAND_PRICING                = buildLadder(8,  [15, 35, 79,  159, 319]);   // creative/design tooling + asset management
export const PRODUCTS_PRICING             = buildLadder(15, [19, 49, 109, 229, 459]);   // PIM: catalog, QC, specs, media, SKUs
export const PAYMENTS_OPS_PRICING         = buildLadder(8,  [9,  25, 59,  129, 259]);   // reconciliation/ops around the fee engine, not the rails themselves
export const INVENTORY_FULFILMENT_PRICING = buildLadder(13, [19, 49, 119, 249, 499]);   // warehousing + carrier integrations
export const MARKETING_PRICING            = buildLadder(17, [19, 55, 129, 269, 549]);   // many ad-platform integrations
export const SALES_PRICING                = buildLadder(11, [15, 39, 89,  189, 379]);   // CRM/pipeline tooling
export const CUSTOMER_SERVICE_PRICING     = buildLadder(10, [12, 29, 69,  149, 299]);   // helpdesk/ticketing; the AI chatbot itself is priced separately (1509)
export const BOOKKEEPING_PRICING          = buildLadder(17, [25, 59, 129, 269, 549]);   // accounting-grade, highest compliance stakes of the generic set
export const EMPLOYEES_PRICING            = buildLadder(13, [19, 49, 109, 229, 459]);   // payroll + HR compliance-adjacent
export const BUSINESS_MANAGEMENT_PRICING  = buildLadder(12, [15, 39, 89,  189, 379]);   // internal ops: PM, dashboards, SOPs
export const ANALYTICS_PRICING            = buildLadder(16, [15, 39, 99,  219, 449]);   // reporting pipelines across GA/CAC/LTV/ROAS etc
export const AI_CEO_SYSTEM_PRICING        = buildLadder(14, [49, 119, 259, 549, 999]);  // exec-level AI synthesis across every other section — priced like it

type CategoryPricingEntry = { table: CategoryPricingTable; total: number };

/* categoryId → its ladder. 3, 15, 16 deliberately absent — see the
   note above. */
export const CATEGORY_PRICING: Record<number, CategoryPricingEntry> = {
  1:  { table: LEGAL_BUSINESS_PRICING,       total: 17 },
  2:  { table: DOMAIN_PRICING,               total: 6  },
  4:  { table: BRAND_PRICING,                total: 8  },
  5:  { table: PRODUCTS_PRICING,             total: 15 },
  6:  { table: PAYMENTS_OPS_PRICING,         total: 8  },
  7:  { table: INVENTORY_FULFILMENT_PRICING, total: 13 },
  8:  { table: MARKETING_PRICING,            total: 17 },
  9:  { table: SALES_PRICING,                total: 11 },
  10: { table: CUSTOMER_SERVICE_PRICING,     total: 10 },
  11: { table: BOOKKEEPING_PRICING,          total: 17 },
  12: { table: EMPLOYEES_PRICING,            total: 13 },
  13: { table: BUSINESS_MANAGEMENT_PRICING,  total: 12 },
  14: { table: ANALYTICS_PRICING,            total: 16 },
  17: { table: AI_CEO_SYSTEM_PRICING,        total: 14 },
};

export const categoryPricingFor = (categoryId: number, tier: SubscriptionTier) => {
  const cat = CATEGORY_PRICING[categoryId];
  return cat ? cat.table.find((r) => r.tierId === tierByName(tier).id) ?? null : null;
};

/* How many of this category's subcategories a given tier unlocks.
   Replaces the old minimumCategoryTierForSubcats — there's no
   picker to size a floor against anymore, the tier just tells you
   the count directly. */
export const includedSubcategoryCount = (categoryId: number, tier: SubscriptionTier): number =>
  categoryPricingFor(categoryId, tier)?.includedSubcategories ?? 0;

/* Prices ONE category on ITS OWN ladder, for ONE tier. THE TIER IS
   THE FEATURE SET: there is no subcategory picker on this
   entrypoint anymore, and so no floor to lift the chosen tier to —
   every tier is directly biddable and prices independently of every
   other tier, which is what makes four different cards show four
   different prices instead of all collapsing onto whichever tier a
   selection happened to force. */
export const computeCategorySubscription = ({
  categoryId,
  tier = "basic",
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  categoryId: number;
  tier?: SubscriptionTier;
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  const cat = CATEGORY_PRICING[categoryId];
  if (!cat) return null;

  const salesReasons = needsSalesCall(customTerms);
  if (salesReasons.length) {
    return {
      product: "category" as ProductType,
      categoryId,
      categoryName: featureName(categoryId) ?? `Category ${categoryId}`,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      totalCharged: null as number | null,
    };
  }

  const row = categoryPricingFor(categoryId, tier)!;
  const monthlyTotal = r2(row.monthlyBase);
  const cadenced = applyCadence(monthlyTotal, cadence);

  return {
    product: "category" as ProductType,
    categoryId,
    categoryName: featureName(categoryId) ?? `Category ${categoryId}`,
    tier,
    tierName: tierByName(tier).label,
    tierWasLifted: false as const, // kept so old callers don't break; never true now — nothing to lift against
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cadenced.cadenceLabel,
    cadenceShort: cadenced.cadenceShort,
    monthEquivalent: cadenced.monthEquivalent,
    savingsVsMonthly: cadenced.savingsVsMonthly,

    includedSubcategories: row.includedSubcategories,
    totalSubcategories: cat.total,
    unlocksEverything: row.includedSubcategories >= cat.total,

    monthlyBase: row.monthlyBase,
    monthlyTotal,
    recurringCharge: cadenced.recurringCharge,
    ...settle(cadenced.recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

/* ════════════════════════════════════════════════════════════
   EBUOSYSTEM — "Ebuos Collection": every priced section at once,
   on one tier, with a bundle discount because buying all 14
   sections separately would cost more than buying the platform.

   Same tier-is-the-feature-set model as a single category:
   featureIds defaults to every priced section (there's nothing to
   toggle on this entrypoint by default), and the whole bundle is
   priced on ONE tier — Basic gets Basic on every section, Elite
   gets Elite on every section. 3 (Website), 15 (AI & Automation)
   and 16 (Security) are still their own products with their own
   state elsewhere in this slice, so they're never part of the
   Collection sum — see handledElsewhere on the return.
   ════════════════════════════════════════════════════════════ */

/* Indexed by tierId. The bigger the tier, the bigger the bundle
   saving — that's where the sections overlap most in what a
   merchant actually uses day to day. */
export const EBUOS_COLLECTION_DISCOUNT = [0.30, 0.35, 0.40, 0.45, 0.50] as const;
export const EBUOS_COLLECTION_IDS = Object.keys(CATEGORY_PRICING).map(Number);

export const computeEbuosSubscription = ({
  tier = "basic",
  featureIds = EBUOS_COLLECTION_IDS,
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  tier?: SubscriptionTier;
  featureIds?: number[];
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  const requested = Array.from(new Set(featureIds)).filter((id) => !!CATEGORY_PRICING[id]);

  const salesReasons = needsSalesCall(customTerms);
  if (salesReasons.length) {
    return {
      product: "ebuosystem" as ProductType,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      activeFeatures: requested,
      totalCharged: null as number | null,
    };
  }

  const isCategoryPriced = (
    q: ReturnType<typeof computeCategorySubscription>,
  ): q is Extract<NonNullable<typeof q>, { monthlyTotal: number }> => !!q && "monthlyTotal" in q;

  const parts = requested
    .map((id) => computeCategorySubscription({ categoryId: id, tier }))
    .filter(isCategoryPriced);

  const handledElsewhere = EBUOS_FEATURES.map((f) => f.id).filter((id) => !CATEGORY_PRICING[id]);

  const listMonthlyTotal = r2(parts.reduce((sum, p) => sum + p.monthlyTotal, 0));
  const bundleDiscountPercent = EBUOS_COLLECTION_DISCOUNT[tierByName(tier).id];
  const monthlyTotal = r2(listMonthlyTotal * (1 - bundleDiscountPercent));
  const cadenced = applyCadence(monthlyTotal, cadence);

  return {
    product: "ebuosystem" as ProductType,
    tier,
    tierName: tierByName(tier).label,
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cadenced.cadenceLabel,
    cadenceShort: cadenced.cadenceShort,
    monthEquivalent: cadenced.monthEquivalent,
    savingsVsMonthly: cadenced.savingsVsMonthly,

    activeFeatures: parts.map((p) => p.categoryId),
    handledElsewhere,
    includedSubcategoryTotal: parts.reduce((sum, p) => sum + p.includedSubcategories, 0),
    totalSubcategoryTotal: parts.reduce((sum, p) => sum + p.totalSubcategories, 0),
    perCategory: parts.map((p) => ({
      categoryId: p.categoryId,
      categoryName: p.categoryName,
      tierName: p.tierName,
      includedSubcategories: p.includedSubcategories,
      totalSubcategories: p.totalSubcategories,
      monthlyTotal: p.monthlyTotal,
    })),

    listMonthlyTotal,
    bundleDiscountPercent,
    monthlyBase: monthlyTotal,
    monthlyTotal,
    recurringCharge: cadenced.recurringCharge,
    ...settle(cadenced.recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

/* ════════════════════════════════════════════════════════════
   DIGITAL CREATOR — appSubcategories id 1501, under AI &
   Automation. This is the content engine: it writes, schedules
   and publishes posts across the merchant's social channels.

   (It used to be gated on "customers handled", which was really
   describing the AI chat assistant. That job now lives in
   CUSTOMER_AGENT below, so Digital Creator is gated on what it
   actually consumes: connected channels and post volume — the
   same shape Buffer / Later / Hootsuite use.)

   - Base content rate is 6 posts/day (DIGITAL_CREATOR_POSTS_PER_DAY_BASE).
     Included posts = base * postsMultiplier * cadence.days.
   - Each post is charged at the tier's pricePerPost, discounted by
     cadence.discountVsDaily — longer commitments cost less per post.
   - Channels beyond the tier's allowance are billed monthly and
     converted onto the cadence with the usual factor.
   ════════════════════════════════════════════════════════════ */

export const DIGITAL_CREATOR_TOOL = { id: 1501, name: "Digital creator" } as const;
export const DIGITAL_CREATOR_POSTS_PER_DAY_BASE = 6;
export const DIGITAL_CREATOR_ENTERPRISE_CHANNELS = 250;

export const DIGITAL_CREATOR_PRICING = [
  { tierId: 0, postsMultiplier: 1,  includedChannels: 2,   maxChannels: 3,   extraChannelMonthly: 9,  pricePerPost: 0.12, videoMinutesPerMonth: 10,  brandVoices: 1,  approvalQueue: false, apiAccess: false, includedPlatformTypes: 1, extraPlatformTypeMonthly: 15 }, // starter — Facebook only
  { tierId: 1, postsMultiplier: 2,  includedChannels: 5,   maxChannels: 10,  extraChannelMonthly: 8,  pricePerPost: 0.16, videoMinutesPerMonth: 60,  brandVoices: 2,  approvalQueue: false, apiAccess: false, includedPlatformTypes: 2, extraPlatformTypeMonthly: 12 }, // basic — +Instagram
  { tierId: 2, postsMultiplier: 4,  includedChannels: 15,  maxChannels: 30,  extraChannelMonthly: 7,  pricePerPost: 0.19, videoMinutesPerMonth: 240, brandVoices: 5,  approvalQueue: true,  apiAccess: true,  includedPlatformTypes: 4, extraPlatformTypeMonthly: 9  }, // premium
  { tierId: 3, postsMultiplier: 8,  includedChannels: 40,  maxChannels: 80,  extraChannelMonthly: 6,  pricePerPost: 0.23, videoMinutesPerMonth: 600, brandVoices: 15, approvalQueue: true,  apiAccess: true,  includedPlatformTypes: 6, extraPlatformTypeMonthly: 6  }, // titanium
  { tierId: 4, postsMultiplier: 16, includedChannels: 100, maxChannels: 250, extraChannelMonthly: 5,  pricePerPost: 0.24, videoMinutesPerMonth: 1800, brandVoices: 50, approvalQueue: true, apiAccess: true,  includedPlatformTypes: 7, extraPlatformTypeMonthly: 0  }, // elite — all platforms
] as const;

export const digitalCreatorPricingFor = (tier: SubscriptionTier) => {
  const tierId = tierByName(tier).id;
  return DIGITAL_CREATOR_PRICING.find((p) => p.tierId === tierId)!;
};

export const minimumDigitalCreatorTierForChannels = (channels: number): SubscriptionTier =>
  lowestTierWhere(DIGITAL_CREATOR_PRICING, (p) => channels <= p.maxChannels);

export const computeDigitalCreatorSubscription = ({
  tier,
  channelsByPlatform = { 1: 1 },   // { platformTypeId: channelCount } — e.g. {1: 2, 3: 1} = 2 FB pages + 1 TikTok
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  tier?: SubscriptionTier;
  channelsByPlatform?: Record<number, number>;
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  /* Clean the input: drop unknown platform ids, zero/negative counts,
     floor to whole channels. This map IS the platform selection now —
     a platform with no channels simply isn't in use, no separate toggle. */
  const cleanChannels: Record<number, number> = {};
  for (const [idStr, count] of Object.entries(channelsByPlatform)) {
    const id = Number(idStr);
    const n = Math.floor(count);
    if (n > 0 && SOCIAL_MEDIA_TYPES.some((s) => s.id === id)) cleanChannels[id] = n;
  }
  const requestedPlatformTypes = Object.keys(cleanChannels).map(Number);
  const connectedChannels = Math.max(1, Object.values(cleanChannels).reduce((a, b) => a + b, 0));

  const salesReasons = needsSalesCall(customTerms, connectedChannels, DIGITAL_CREATOR_ENTERPRISE_CHANNELS, "connected channels");
  if (salesReasons.length) {
    return {
      product: "digitalCreator" as ProductType,
      tool: DIGITAL_CREATOR_TOOL,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      connectedChannels,
      totalCharged: null as number | null,
    };
  }

  const chosen: SubscriptionTier = tier ?? minimumDigitalCreatorTierForChannels(connectedChannels);
  const floor = minimumDigitalCreatorTierForChannels(connectedChannels);
  const effectiveTier = liftTier(chosen, floor);
  const spec = digitalCreatorPricingFor(effectiveTier);

  const cad = tierTypeByName(cadence);
  const postsPerDay = DIGITAL_CREATOR_POSTS_PER_DAY_BASE * spec.postsMultiplier;
  const postsIncluded = postsPerDay * cad.days;

  /* Posts are split across channels evenly, then each platform's
     slice is priced at the tier's base rate × that platform's weight —
     this is the actual fix: a YouTube post costs more than a tweet. */
  const postsPerChannel = postsIncluded / connectedChannels;
  let contentCharge = 0;
  const perPlatformBreakdown: { platformId: number; label: string; channels: number; postsAllocated: number; charge: number }[] = [];
  for (const [idStr, count] of Object.entries(cleanChannels)) {
    const id = Number(idStr);
    const platform = SOCIAL_MEDIA_TYPES.find((s) => s.id === id)!;
    const postsForPlatform = postsPerChannel * count;
    const charge = r2(postsForPlatform * spec.pricePerPost * platform.postPriceMultiplier * (1 - cad.discountVsDaily));
    contentCharge += charge;
    perPlatformBreakdown.push({ platformId: id, label: platform.label, channels: count, postsAllocated: Math.round(postsForPlatform), charge });
  }
  contentCharge = r2(contentCharge);

  const extraChannels = Math.max(0, connectedChannels - spec.includedChannels);
  const channelCharge = r2(extraChannels * spec.extraChannelMonthly * cad.factor);

  const extraPlatformTypes = Math.max(0, requestedPlatformTypes.length - spec.includedPlatformTypes);
  const platformTypeCharge = r2(extraPlatformTypes * spec.extraPlatformTypeMonthly * cad.factor);

  const recurringCharge = r2(contentCharge + channelCharge + platformTypeCharge);
  const monthEquivalent = r2(recurringCharge / cad.monthsCovered);

  return {
    product: "digitalCreator" as ProductType,
    tool: DIGITAL_CREATOR_TOOL,
    tier: effectiveTier,
    tierName: tierByName(effectiveTier).label,
    tierWasLifted: effectiveTier !== chosen,
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cad.label,
    cadenceShort: cad.short,
    monthEquivalent,

    connectedChannels,
    channelsByPlatform: cleanChannels,
    includedChannels: spec.includedChannels,
    extraChannels,
    extraChannelMonthly: spec.extraChannelMonthly,
    channelCharge,
    requiresUpgradeTo: connectedChannels > spec.maxChannels ? minimumDigitalCreatorTierForChannels(connectedChannels) : null,

    platformTypeIds: requestedPlatformTypes,
    includedPlatformTypes: spec.includedPlatformTypes,
    extraPlatformTypes,
    extraPlatformTypeMonthly: spec.extraPlatformTypeMonthly,
    platformTypeCharge,
    perPlatformBreakdown,

    postsPerDay,
    postsIncluded,
    pricePerPost: spec.pricePerPost,
    contentCharge,
    videoMinutesPerMonth: spec.videoMinutesPerMonth,
    brandVoices: spec.brandVoices,
    approvalQueue: spec.approvalQueue,
    apiAccess: spec.apiAccess,

    recurringCharge,
    ...settle(recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

type DigitalCreatorQuote = ReturnType<typeof computeDigitalCreatorSubscription>;

export const priceEveryDigitalCreatorTier = (args: Omit<Parameters<typeof computeDigitalCreatorSubscription>[0], "tier">) =>
  ALL_TIER_ORDER.reduce((acc, t) => {
    const r = computeDigitalCreatorSubscription({ ...args, tier: t });
    acc[t] = {
      totalCharged: r.totalCharged,
      recurringCharge: "recurringCharge" in r ? r.recurringCharge : 0,
      postsIncluded: "postsIncluded" in r ? r.postsIncluded : 0,
    };
    return acc;
  }, {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; postsIncluded: number }>);

/* ════════════════════════════════════════════════════════════
   CUSTOMER AGENT — appSubcategories 1509 (AI customer support) /
   1009 (AI chatbot). The AI that actually talks to the merchant's
   customers: live chat, email, socials, and voice on premium+.

   Priced on BUSINESS SCALE — resolved conversations per month,
   from 10 up into the millions — using graduated bands, exactly
   like shortstays' service fee gets cheaper as the booking gets
   bigger. The tier buys the platform (channels, languages, seats,
   escalation, retention) plus an included allowance; everything
   past the allowance is metered.

   Over CUSTOMER_AGENT_ENTERPRISE_SCALE (1M conversations/month)
   it stops being a price list and becomes a sales call.
   ════════════════════════════════════════════════════════════ */

export const CUSTOMER_AGENT_TOOL = { id: 1509, name: "AI customer agent" } as const;
export const CUSTOMER_AGENT_MIN_SCALE = 10;
export const CUSTOMER_AGENT_ENTERPRISE_SCALE = 1_000_000;
export const CUSTOMER_AGENT_EXTRA_SEAT_MONTHLY = 12;

/* Benchmarked against per-resolution AI support pricing (~$0.99 at
   the low end, cents at contact-centre volume). Graduated. */
export const CUSTOMER_AGENT_BANDS: readonly ScaleBand[] = [
  { upTo: 500,       unitPrice: 0.90,  label: "First 500 conversations" },
  { upTo: 5_000,     unitPrice: 0.65,  label: "501 – 5,000" },
  { upTo: 50_000,    unitPrice: 0.42,  label: "5,001 – 50,000" },
  { upTo: 250_000,   unitPrice: 0.28,  label: "50,001 – 250,000" },
  { upTo: 1_000_000, unitPrice: 0.175, label: "250,001 – 1,000,000" },
] as const;

/* Voice is a separate meter — telephony + speech cost real money
   per minute, so it never gets folded into the conversation rate. */
export const CUSTOMER_AGENT_VOICE_BANDS: readonly ScaleBand[] = [
  { upTo: 1_000,   unitPrice: 0.14, label: "First 1,000 voice minutes" },
  { upTo: 10_000,  unitPrice: 0.11, label: "1,001 – 10,000" },
  { upTo: 100_000, unitPrice: 0.08, label: "10,001 – 100,000" },
  { upTo: 1_000_000, unitPrice: 0.06, label: "Over 100,000" },
] as const;

/* ── Outcome pricing ──────────────────────────────────────────
   Not every conversation is worth the same to the merchant, so it
   shouldn't cost the same:

   - RESOLVED (agent answered, nothing risky happened) bills at the
     plain banded rate from CUSTOMER_AGENT_BANDS. This is the
     default bucket and needs no adjustment.
   - ESCALATED (needs_human / chat.escalation.created) means the
     agent didn't finish the job — a human still had the
     conversation. Billing it like a resolved conversation would
     charge full price for work we didn't do, so it's billed at a
     small fraction of the banded rate instead of full price or
     free, so a merchant can't zero out their bill by having the
     agent escalate everything.
   - ACTIONED (chat.action.cancel_requested / refund_requested
     fired) is a conversation where the agent didn't just talk, it
     changed something with real liability attached. That carries
     more risk than a Q&A reply, so it gets a flat surcharge on top
     of the normal banded rate. */
export const CUSTOMER_AGENT_ESCALATION_RATE_FACTOR = 0.15; // escalated conversations bill at 15% of the banded rate
export const CUSTOMER_AGENT_ACTION_SURCHARGE = 0.12; // flat $ added per actioned conversation, on top of the banded rate

export const CUSTOMER_AGENT_PRICING = [
  { tierId: 0, platformBase: 0,     includedConversations: 50,     maxConversations: 500,       channels: 1,  languages: 1,  seats: 1,   humanEscalation: false, voice: false, crmSync: false, customKnowledge: false, historyDays: 30,   slaMinutes: null }, // starter
  { tierId: 1, platformBase: 49,    includedConversations: 400,    maxConversations: 5_000,     channels: 3,  languages: 5,  seats: 5,   humanEscalation: true,  voice: false, crmSync: false, customKnowledge: true,  historyDays: 90,   slaMinutes: null }, // basic
  { tierId: 2, platformBase: 199,   includedConversations: 2_000,  maxConversations: 50_000,    channels: 6,  languages: 25, seats: 25,  humanEscalation: true,  voice: true,  crmSync: true,  customKnowledge: true,  historyDays: 365,  slaMinutes: 240 }, // premium
  { tierId: 3, platformBase: 599,   includedConversations: 8_000,  maxConversations: 250_000,   channels: 12, languages: 50, seats: 100, humanEscalation: true,  voice: true,  crmSync: true,  customKnowledge: true,  historyDays: 730,  slaMinutes: 60 },  // titanium
  { tierId: 4, platformBase: 1_499, includedConversations: 30_000, maxConversations: 1_000_000, channels: -1, languages: -1, seats: -1,  humanEscalation: true,  voice: true,  crmSync: true,  customKnowledge: true,  historyDays: 1_825, slaMinutes: 15 },  // elite
] as const;

export const customerAgentPricingFor = (tier: SubscriptionTier) => {
  const tierId = tierByName(tier).id;
  return CUSTOMER_AGENT_PRICING.find((p) => p.tierId === tierId)!;
};

export const minimumCustomerAgentTierForScale = (conversationsPerMonth: number): SubscriptionTier =>
  lowestTierWhere(CUSTOMER_AGENT_PRICING, (p) => conversationsPerMonth <= p.maxConversations);

export const computeCustomerAgentSubscription = ({
  tier,
  conversationsPerMonth = 0,
  voiceMinutes = 0,
  seats = 1,
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  tier?: SubscriptionTier;
  conversationsPerMonth?: number;
  voiceMinutes?: number;
  seats?: number;
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  const raw = Math.max(0, Math.floor(conversationsPerMonth));
  /* Anything at all counts as being in business — the scale floor
     is 10, so a merchant with 3 conversations is priced like 10
     rather than falling off the bottom of the first band. */
  const scale = raw === 0 ? 0 : Math.max(CUSTOMER_AGENT_MIN_SCALE, raw);

  const salesReasons = needsSalesCall(customTerms, scale, CUSTOMER_AGENT_ENTERPRISE_SCALE, "conversations/month");
  if (salesReasons.length) {
    return {
      product: "customerAgent" as ProductType,
      tool: CUSTOMER_AGENT_TOOL,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      conversationsPerMonth: scale,
      totalCharged: null as number | null,
    };
  }

  const chosen: SubscriptionTier = tier ?? minimumCustomerAgentTierForScale(scale);
  const floor = minimumCustomerAgentTierForScale(scale);
  const effectiveTier = liftTier(chosen, floor);
  const spec = customerAgentPricingFor(effectiveTier);

  const conversationQuote = priceScale(scale, CUSTOMER_AGENT_BANDS, spec.includedConversations);
  const billableVoice = spec.voice ? Math.max(0, Math.floor(voiceMinutes)) : 0;
  const voiceQuote = priceScale(billableVoice, CUSTOMER_AGENT_VOICE_BANDS);

  const requestedSeats = Math.max(1, Math.floor(seats));
  const seatCap = spec.seats === -1 ? Infinity : spec.seats;
  const extraSeats = Math.max(0, requestedSeats - (spec.seats === -1 ? requestedSeats : spec.seats));
  const seatCharge = r2(extraSeats * CUSTOMER_AGENT_EXTRA_SEAT_MONTHLY);

  const monthlyTotal = r2(spec.platformBase + conversationQuote.total + voiceQuote.total + seatCharge);
  const cadenced = applyCadence(monthlyTotal, cadence);

  return {
    product: "customerAgent" as ProductType,
    tool: CUSTOMER_AGENT_TOOL,
    tier: effectiveTier,
    tierName: tierByName(effectiveTier).label,
    tierWasLifted: effectiveTier !== chosen,
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cadenced.cadenceLabel,
    cadenceShort: cadenced.cadenceShort,
    monthEquivalent: cadenced.monthEquivalent,
    savingsVsMonthly: cadenced.savingsVsMonthly,

    conversationsPerMonth: scale,
    includedConversations: spec.includedConversations,
    billableConversations: conversationQuote.billableUnits,
    conversationCharge: conversationQuote.total,
    effectiveCostPerConversation: conversationQuote.effectiveUnitRate,
    conversationBreakdown: conversationQuote.breakdown,
    requiresUpgradeTo: scale > spec.maxConversations ? minimumCustomerAgentTierForScale(scale) : null,

    voiceEnabled: spec.voice,
    voiceMinutes: billableVoice,
    voiceCharge: voiceQuote.total,
    voiceBreakdown: voiceQuote.breakdown,

    seats: requestedSeats,
    includedSeats: spec.seats,
    extraSeats,
    seatCharge,
    seatCapReached: requestedSeats > seatCap,

    channels: spec.channels,
    languages: spec.languages,
    humanEscalation: spec.humanEscalation,
    crmSync: spec.crmSync,
    customKnowledge: spec.customKnowledge,
    historyDays: spec.historyDays,
    slaMinutes: spec.slaMinutes,

    platformBase: spec.platformBase,
    monthlyTotal,
    recurringCharge: cadenced.recurringCharge,
    ...settle(cadenced.recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

export const priceEveryCustomerAgentTier = (args: Omit<Parameters<typeof computeCustomerAgentSubscription>[0], "tier">) =>
  ALL_TIER_ORDER.reduce((acc, t) => {
    const r = computeCustomerAgentSubscription({ ...args, tier: t });
    acc[t] = {
      totalCharged: r.totalCharged,
      recurringCharge: "recurringCharge" in r ? r.recurringCharge : 0,
      costPerConversation: "effectiveCostPerConversation" in r ? r.effectiveCostPerConversation : 0,
    };
    return acc;
  }, {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; costPerConversation: number }>);

/* Cheapest tier that can actually serve this scale — the floor is
   never the answer by itself, because a bigger tier's allowance can
   undercut a smaller tier's metered bill. */
export const recommendCustomerAgentTier = (args: Omit<Parameters<typeof computeCustomerAgentSubscription>[0], "tier">): SubscriptionTier => {
  const floor = minimumCustomerAgentTierForScale(Math.max(0, Math.floor(args.conversationsPerMonth ?? 0)));
  const priced = priceEveryCustomerAgentTier(args);
  return ALL_TIER_ORDER
    .filter((t) => tierByName(t).id >= tierByName(floor).id)
    .reduce((best, t) => ((priced[t].totalCharged ?? Infinity) < (priced[best].totalCharged ?? Infinity) ? t : best), floor);
};

/* ════════════════════════════════════════════════════════════
   AUTOMATION — everything else under AI & Automation (1502-1516):
   workflows, integrations, database jobs, CRM/marketing/sales
   automation, reporting, approvals.

   Priced the way every automation platform on earth prices:
   - The tier buys capacity: how many workflows can be live at once,
     how often they're allowed to run, premium connectors, version
     history, sandbox. That's the platformBase.
   - Runs are metered per task (one task = one step executed in one
     run), graduated so volume gets cheap fast — 2¢ at hobby volume
     down to fractions of a cent at 10M.
   - Over AUTOMATION_ENTERPRISE_TASKS (10M tasks/month) it's a
     sales conversation about dedicated infrastructure.
   ════════════════════════════════════════════════════════════ */

export const AUTOMATION_ENTERPRISE_TASKS = 10_000_000;

/* Subcategory ids 1502-1516. 1501 belongs to Digital Creator and
   1509 to the Customer Agent, so neither appears here. */
export const AUTOMATION_MODULES = [
  { id: 1502, name: "Workflow automation",           minTierId: 0 },
  { id: 1504, name: "App integration automation",    minTierId: 0 },
  { id: 1511, name: "Inventory alerts",              minTierId: 0 },
  { id: 1503, name: "Process automation",            minTierId: 1 },
  { id: 1505, name: "Database automation",           minTierId: 1 },
  { id: 1506, name: "Knowledge base management",     minTierId: 1 },
  { id: 1507, name: "CRM automation",                minTierId: 2 },
  { id: 1508, name: "Marketing automation",          minTierId: 2 },
  { id: 1510, name: "Sales automation",              minTierId: 2 },
  { id: 1513, name: "Reporting automation",          minTierId: 2 },
  { id: 1512, name: "Accounting automation",         minTierId: 3 },
  { id: 1514, name: "Employee workflow automation",  minTierId: 3 },
  { id: 1515, name: "AI business dashboard",         minTierId: 3 },
  { id: 1516, name: "Human approval system",         minTierId: 4 },
] as const;

export const automationModuleName = (id: number): string | undefined =>
  AUTOMATION_MODULES.find((m) => m.id === id)?.name;

export const AUTOMATION_TASK_BANDS: readonly ScaleBand[] = [
  { upTo: 10_000,     unitPrice: 0.020,  label: "First 10,000 tasks" },
  { upTo: 100_000,    unitPrice: 0.011,  label: "10,001 – 100,000" },
  { upTo: 1_000_000,  unitPrice: 0.005,  label: "100,001 – 1,000,000" },
  { upTo: 10_000_000, unitPrice: 0.0022, label: "1,000,001 – 10,000,000" },
] as const;

export const AUTOMATION_PRICING = [
  { tierId: 0, platformBase: 0,   includedTasks: 500,       activeWorkflows: 3,   minIntervalMinutes: 60, premiumConnectors: false, versionHistory: false, sandbox: false, aiSteps: false,  logRetentionDays: 7,   concurrentRuns: 1 },  // starter
  { tierId: 1, platformBase: 29,  includedTasks: 5_000,     activeWorkflows: 25,  minIntervalMinutes: 15, premiumConnectors: false, versionHistory: false, sandbox: false, aiSteps: true,   logRetentionDays: 30,  concurrentRuns: 3 },  // basic
  { tierId: 2, platformBase: 89,  includedTasks: 50_000,    activeWorkflows: 150, minIntervalMinutes: 5,  premiumConnectors: true,  versionHistory: true,  sandbox: false, aiSteps: true,   logRetentionDays: 90,  concurrentRuns: 10 }, // premium
  { tierId: 3, platformBase: 249, includedTasks: 400_000,   activeWorkflows: 750, minIntervalMinutes: 1,  premiumConnectors: true,  versionHistory: true,  sandbox: true,  aiSteps: true,   logRetentionDays: 365, concurrentRuns: 40 }, // titanium
  { tierId: 4, platformBase: 699, includedTasks: 2_000_000, activeWorkflows: -1,  minIntervalMinutes: 0,  premiumConnectors: true,  versionHistory: true,  sandbox: true,  aiSteps: true,   logRetentionDays: 730, concurrentRuns: 200 }, // elite (0 = instant webhooks)
] as const;

export const automationPricingFor = (tier: SubscriptionTier) => {
  const tierId = tierByName(tier).id;
  return AUTOMATION_PRICING.find((p) => p.tierId === tierId)!;
};

export const minimumAutomationTierForWorkflows = (activeWorkflows: number): SubscriptionTier =>
  lowestTierWhere(AUTOMATION_PRICING, (p) => p.activeWorkflows === -1 || activeWorkflows <= p.activeWorkflows);

export const minimumAutomationTierFor = (activeWorkflows: number, moduleIds: number[] = []): SubscriptionTier => {
  const byWorkflows = minimumAutomationTierForWorkflows(activeWorkflows);
  const byModules = minTierForModules(AUTOMATION_MODULES, moduleIds);
  return liftTier(byWorkflows, byModules);
};

export const computeAutomationSubscription = ({
  tier,
  tasksPerMonth = 0,
  activeWorkflows = 1,
  moduleIds = [],
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  tier?: SubscriptionTier;
  tasksPerMonth?: number;
  activeWorkflows?: number;
  moduleIds?: number[];
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  const tasks = Math.max(0, Math.floor(tasksPerMonth));
  const workflows = Math.max(0, Math.floor(activeWorkflows));

  const salesReasons = needsSalesCall(customTerms, tasks, AUTOMATION_ENTERPRISE_TASKS, "tasks/month");
  if (salesReasons.length) {
    return {
      product: "automation" as ProductType,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      tasksPerMonth: tasks,
      activeWorkflows: workflows,
      totalCharged: null as number | null,
    };
  }

  const floor = minimumAutomationTierFor(workflows, moduleIds);
  const chosen: SubscriptionTier = tier ?? floor;
  const effectiveTier = liftTier(chosen, floor);
  const spec = automationPricingFor(effectiveTier);

  const { known, active, blocked } = splitByTier(AUTOMATION_MODULES, moduleIds, effectiveTier);
  const taskQuote = priceScale(tasks, AUTOMATION_TASK_BANDS, spec.includedTasks);

  const monthlyTotal = r2(spec.platformBase + taskQuote.total);
  const cadenced = applyCadence(monthlyTotal, cadence);

  return {
    product: "automation" as ProductType,
    tier: effectiveTier,
    tierName: tierByName(effectiveTier).label,
    tierWasLifted: effectiveTier !== chosen,
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cadenced.cadenceLabel,
    cadenceShort: cadenced.cadenceShort,
    monthEquivalent: cadenced.monthEquivalent,
    savingsVsMonthly: cadenced.savingsVsMonthly,

    tasksPerMonth: tasks,
    includedTasks: spec.includedTasks,
    billableTasks: taskQuote.billableUnits,
    taskCharge: taskQuote.total,
    effectiveCostPerTask: taskQuote.effectiveUnitRate,
    taskBreakdown: taskQuote.breakdown,

    activeWorkflows: workflows,
    workflowAllowance: spec.activeWorkflows,
    workflowCapReached: spec.activeWorkflows !== -1 && workflows > spec.activeWorkflows,

    requestedModules: known,
    activeModules: active,
    blockedModules: blocked,
    requiresUpgradeTo:
      blocked.length || (spec.activeWorkflows !== -1 && workflows > spec.activeWorkflows)
        ? minimumAutomationTierFor(workflows, known)
        : null,

    minIntervalMinutes: spec.minIntervalMinutes,
    premiumConnectors: spec.premiumConnectors,
    versionHistory: spec.versionHistory,
    sandbox: spec.sandbox,
    aiSteps: spec.aiSteps,
    logRetentionDays: spec.logRetentionDays,
    concurrentRuns: spec.concurrentRuns,

    platformBase: spec.platformBase,
    monthlyTotal,
    recurringCharge: cadenced.recurringCharge,
    ...settle(cadenced.recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

export const priceEveryAutomationTier = (args: Omit<Parameters<typeof computeAutomationSubscription>[0], "tier">) =>
  ALL_TIER_ORDER.reduce((acc, t) => {
    const r = computeAutomationSubscription({ ...args, tier: t });
    acc[t] = {
      totalCharged: r.totalCharged,
      recurringCharge: "recurringCharge" in r ? r.recurringCharge : 0,
      blocked: "blockedModules" in r ? r.blockedModules.length : 0,
    };
    return acc;
  }, {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; blocked: number }>);

export const recommendAutomationTier = (args: Omit<Parameters<typeof computeAutomationSubscription>[0], "tier">): SubscriptionTier => {
  const floor = minimumAutomationTierFor(args.activeWorkflows ?? 1, args.moduleIds ?? []);
  const priced = priceEveryAutomationTier(args);
  return ALL_TIER_ORDER
    .filter((t) => tierByName(t).id >= tierByName(floor).id)
    .reduce((best, t) => ((priced[t].totalCharged ?? Infinity) < (priced[best].totalCharged ?? Infinity) ? t : best), floor);
};

/* ════════════════════════════════════════════════════════════
   SECURITY — top-level section 16, subcategories 1601-1609.

   Same scale logic: what a security posture costs is a function of
   how much surface there is to defend. The unit is a protected
   endpoint — one staff identity, device, or workload — and the
   per-endpoint rate falls as the estate grows, because monitoring
   the 2,000th endpoint costs us far less than the 2nd.

   Backups are a second meter (per GB/month), because storage is a
   real cost that has nothing to do with headcount.
   ════════════════════════════════════════════════════════════ */

export const SECURITY_SECTION = { id: 16, name: "Security" } as const;
export const SECURITY_MIN_ENDPOINTS = 1;
export const SECURITY_ENTERPRISE_ENDPOINTS = 50_000;

export const SECURITY_CONTROLS = [
  { id: 1601, name: "Password manager",         minTierId: 0 },
  { id: 1602, name: "2FA",                      minTierId: 0 },
  { id: 1604, name: "Backups",                  minTierId: 1 },
  { id: 1603, name: "Cloudflare WAF / DDoS",    minTierId: 1 },
  { id: 1605, name: "Access control",           minTierId: 2 },
  { id: 1606, name: "Employee permissions",     minTierId: 2 },
  { id: 1607, name: "Data encryption",          minTierId: 2 },
  { id: 1608, name: "Fraud prevention",         minTierId: 3 },
  { id: 1609, name: "Cybersecurity monitoring", minTierId: 4 },
] as const;

export const securityControlName = (id: number): string | undefined =>
  SECURITY_CONTROLS.find((c) => c.id === id)?.name;

export const SECURITY_ENDPOINT_BANDS: readonly ScaleBand[] = [
  { upTo: 25,     unitPrice: 6.00, label: "First 25 endpoints" },
  { upTo: 250,    unitPrice: 4.25, label: "26 – 250" },
  { upTo: 2_500,  unitPrice: 2.90, label: "251 – 2,500" },
  { upTo: 10_000, unitPrice: 1.85, label: "2,501 – 10,000" },
  { upTo: 50_000, unitPrice: 1.15, label: "10,001 – 50,000" },
] as const;

export const SECURITY_BACKUP_BANDS: readonly ScaleBand[] = [
  { upTo: 100,     unitPrice: 0.12, label: "First 100 GB" },
  { upTo: 1_000,   unitPrice: 0.08, label: "101 – 1,000 GB" },
  { upTo: 10_000,  unitPrice: 0.05, label: "1,001 – 10,000 GB" },
  { upTo: 100_000, unitPrice: 0.03, label: "Over 10,000 GB" },
] as const;

export const SECURITY_PRICING = [
  { tierId: 0, platformBase: 0,   includedEndpoints: 3,     maxEndpoints: 10,     includedBackupGb: 5,      backupRetentionDays: 7,   logRetentionDays: 7,   monitoring: "none",        pentestsPerYear: 0, incidentResponseHours: null, byok: false, auditExport: false }, // starter
  { tierId: 1, platformBase: 39,  includedEndpoints: 10,    maxEndpoints: 100,    includedBackupGb: 100,    backupRetentionDays: 30,  logRetentionDays: 30,  monitoring: "alerts",      pentestsPerYear: 0, incidentResponseHours: null, byok: false, auditExport: false }, // basic
  { tierId: 2, platformBase: 129, includedEndpoints: 50,    maxEndpoints: 1_000,  includedBackupGb: 500,    backupRetentionDays: 90,  logRetentionDays: 90,  monitoring: "daily scan",  pentestsPerYear: 0, incidentResponseHours: 24,   byok: false, auditExport: true },  // premium
  { tierId: 3, platformBase: 349, includedEndpoints: 250,   maxEndpoints: 10_000, includedBackupGb: 2_000,  backupRetentionDays: 365, logRetentionDays: 365, monitoring: "continuous",  pentestsPerYear: 1, incidentResponseHours: 8,    byok: true,  auditExport: true },  // titanium
  { tierId: 4, platformBase: 899, includedEndpoints: 1_000, maxEndpoints: 50_000, includedBackupGb: 10_000, backupRetentionDays: 730, logRetentionDays: 730, monitoring: "24/7 SOC",    pentestsPerYear: 4, incidentResponseHours: 1,    byok: true,  auditExport: true },  // elite
] as const;

export const securityPricingFor = (tier: SubscriptionTier) => {
  const tierId = tierByName(tier).id;
  return SECURITY_PRICING.find((p) => p.tierId === tierId)!;
};

export const minimumSecurityTierForEndpoints = (endpoints: number): SubscriptionTier =>
  lowestTierWhere(SECURITY_PRICING, (p) => endpoints <= p.maxEndpoints);

export const minimumSecurityTierFor = (endpoints: number, controlIds: number[] = []): SubscriptionTier =>
  liftTier(minimumSecurityTierForEndpoints(endpoints), minTierForModules(SECURITY_CONTROLS, controlIds));

export const computeSecuritySubscription = ({
  tier,
  endpoints = 1,
  backupGb = 0,
  controlIds = [],
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
  customTerms = false,
}: {
  tier?: SubscriptionTier;
  endpoints?: number;
  backupGb?: number;
  controlIds?: number[];
  cadence?: TierType;
  customTerms?: boolean;
} & SettleArgs) => {
  const protectedEndpoints = Math.max(SECURITY_MIN_ENDPOINTS, Math.floor(endpoints));
  const storedGb = Math.max(0, Math.floor(backupGb));

  const salesReasons = needsSalesCall(customTerms, protectedEndpoints, SECURITY_ENTERPRISE_ENDPOINTS, "protected endpoints");
  if (salesReasons.length) {
    return {
      product: "security" as ProductType,
      section: SECURITY_SECTION,
      tier: "enterprise" as const,
      tierName: ENTERPRISE.name,
      contactSales: true as const,
      contactSalesReasons: salesReasons,
      contactUrl: ENTERPRISE.contactUrl,
      endpoints: protectedEndpoints,
      totalCharged: null as number | null,
    };
  }

  const floor = minimumSecurityTierFor(protectedEndpoints, controlIds);
  const chosen: SubscriptionTier = tier ?? floor;
  const effectiveTier = liftTier(chosen, floor);
  const spec = securityPricingFor(effectiveTier);

  const { known, active, blocked } = splitByTier(SECURITY_CONTROLS, controlIds, effectiveTier);

  const endpointQuote = priceScale(protectedEndpoints, SECURITY_ENDPOINT_BANDS, spec.includedEndpoints);
  /* Backup storage only bills if the merchant actually has the
     Backups control switched on and unlocked. */
  const backupsOn = active.includes(1604);
  const backupQuote = priceScale(backupsOn ? storedGb : 0, SECURITY_BACKUP_BANDS, spec.includedBackupGb);

  const monthlyTotal = r2(spec.platformBase + endpointQuote.total + backupQuote.total);
  const cadenced = applyCadence(monthlyTotal, cadence);

  return {
    product: "security" as ProductType,
    section: SECURITY_SECTION,
    tier: effectiveTier,
    tierName: tierByName(effectiveTier).label,
    tierWasLifted: effectiveTier !== chosen,
    contactSales: false as const,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence,
    cadenceLabel: cadenced.cadenceLabel,
    cadenceShort: cadenced.cadenceShort,
    monthEquivalent: cadenced.monthEquivalent,
    savingsVsMonthly: cadenced.savingsVsMonthly,

    endpoints: protectedEndpoints,
    includedEndpoints: spec.includedEndpoints,
    billableEndpoints: endpointQuote.billableUnits,
    endpointCharge: endpointQuote.total,
    effectiveCostPerEndpoint: endpointQuote.effectiveUnitRate,
    endpointBreakdown: endpointQuote.breakdown,

    backupsEnabled: backupsOn,
    backupGb: storedGb,
    includedBackupGb: spec.includedBackupGb,
    backupCharge: backupQuote.total,
    backupBreakdown: backupQuote.breakdown,
    backupRetentionDays: spec.backupRetentionDays,

    requestedControls: known,
    activeControls: active,
    blockedControls: blocked,
    requiresUpgradeTo:
      blocked.length || protectedEndpoints > spec.maxEndpoints
        ? minimumSecurityTierFor(protectedEndpoints, known)
        : null,

    monitoring: spec.monitoring,
    pentestsPerYear: spec.pentestsPerYear,
    incidentResponseHours: spec.incidentResponseHours,
    logRetentionDays: spec.logRetentionDays,
    byok: spec.byok,
    auditExport: spec.auditExport,

    platformBase: spec.platformBase,
    monthlyTotal,
    recurringCharge: cadenced.recurringCharge,
    ...settle(cadenced.recurringCharge, { discountApplied, creditsAvailable, vatRate }),
  };
};

export const priceEverySecurityTier = (args: Omit<Parameters<typeof computeSecuritySubscription>[0], "tier">) =>
  ALL_TIER_ORDER.reduce((acc, t) => {
    const r = computeSecuritySubscription({ ...args, tier: t });
    acc[t] = {
      totalCharged: r.totalCharged,
      recurringCharge: "recurringCharge" in r ? r.recurringCharge : 0,
      blocked: "blockedControls" in r ? r.blockedControls.length : 0,
    };
    return acc;
  }, {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; blocked: number }>);

export const recommendSecurityTier = (args: Omit<Parameters<typeof computeSecuritySubscription>[0], "tier">): SubscriptionTier => {
  const floor = minimumSecurityTierFor(args.endpoints ?? 1, args.controlIds ?? []);
  const priced = priceEverySecurityTier(args);
  return ALL_TIER_ORDER
    .filter((t) => tierByName(t).id >= tierByName(floor).id)
    .reduce((best, t) => ((priced[t].totalCharged ?? Infinity) < (priced[best].totalCharged ?? Infinity) ? t : best), floor);
};

/* ════════════════════════════════════════════════════════════
   WEBSITE TYPE PRICING — industry-standard rates for real,
   custom fullstack builds. setupFee is the one-time build cost;
   monthlyHosting is the recurring add-on, billed on the same
   TIER_TYPES cadence as everything else. Keyed by the same
   numeric ids as appSubcategories (301-308, Website category).
   ════════════════════════════════════════════════════════════ */

export const WEBSITE_TYPES = [
  { id: 305, name: "Landing Page",           setupFee: 1_200,  monthlyHosting: 9,  complexity: "low" },
  { id: 306, name: "Portfolio Website",      setupFee: 1_800,  monthlyHosting: 9,  complexity: "low" },
  { id: 307, name: "Blog / Content Website", setupFee: 2_200,  monthlyHosting: 12, complexity: "medium" },
  { id: 304, name: "Custom Website",         setupFee: 4_000,  monthlyHosting: 19, complexity: "medium" },
  { id: 301, name: "Full Stack Website",     setupFee: 8_000,  monthlyHosting: 29, complexity: "high" },
  { id: 302, name: "E-commerce Website",     setupFee: 7_000,  monthlyHosting: 39, complexity: "high" },
  { id: 303, name: "Booking Platform",       setupFee: 9_000,  monthlyHosting: 39, complexity: "high" },
  { id: 308, name: "Marketplace Website",    setupFee: 15_000, monthlyHosting: 79, complexity: "very high" },
] as const;
export const WEBSITE_TYPE_ORDER = WEBSITE_TYPES.map((w) => w.id);
export const websiteTypeById = (typeId: number) => WEBSITE_TYPES.find((w) => w.id === typeId);

/* setupFee = one-time build cost, charged once at signup.
   monthlyHosting = recurring hosting & maintenance, billed on the
   chosen cadence (daily/weekly/monthly/yearly) via cad.factor. */
export const computeWebsiteSubscription = ({
  typeId,
  cadence = "monthly",
  discountApplied = {},
  creditsAvailable = 0,
  vatRate = 0,
}: {
  typeId: number;
  cadence?: TierType;
} & SettleArgs) => {
  const spec = websiteTypeById(typeId);
  if (!spec) return null;

  const cad = tierTypeByName(cadence);
  const hostingCharge = r2(spec.monthlyHosting * cad.factor);
  const subtotal = r2(spec.setupFee + hostingCharge);

  return {
    product: "website" as ProductType,
    typeId: spec.id,
    typeName: spec.name,
    complexity: spec.complexity,

    cadence,
    cadenceLabel: cad.label,
    cadenceShort: cad.short,

    setupFee: spec.setupFee,
    monthlyHosting: spec.monthlyHosting,
    hostingCharge,
    subtotal,
    ...settle(subtotal, { discountApplied, creditsAvailable, vatRate }),
  };
};

/* ════════════════════════════════════════════════════════════
   FIRESTORE HELPERS — one read shape and one write shape for
   every product, so the thunks below stay short.
   ════════════════════════════════════════════════════════════ */

type ProductDoc = Record<string, any>;

const readProductSub = async (merchantId: string, key: string): Promise<ProductDoc | null> => {
  const snap = await getDoc(doc(db, "merchants", merchantId));
  if (!snap.exists()) return null;
  return (snap.data().subscriptions?.[key] || {}) as ProductDoc;
};

const persistProductSub = (merchantId: string, key: string, payload: ProductDoc) =>
  setDoc(
    doc(db, "merchants", merchantId),
    { subscriptions: { [key]: { ...payload, updatedAt: Date.now() } } },
    { merge: true },
  );

const SALES_BLOCK = "This configuration needs a sales conversation before it can be activated.";

// ─── Thunks — ebuosystem ────────────────────────────────
export const calculateEbuosSubscription = createAsyncThunk(
  "subscription/calculateEbuos",
  async (args: Parameters<typeof computeEbuosSubscription>[0]) => computeEbuosSubscription(args)
);

export const fetchAndPriceEbuosSubscription = createAsyncThunk(
  "subscription/fetchAndPriceEbuos",
  async (
    { merchantId, cadenceOverride, vatRate, creditsAvailable, discountApplied }:
    { merchantId: string; cadenceOverride?: TierType; vatRate?: number; creditsAvailable?: number; discountApplied?: DiscountFlags },
    { rejectWithValue }
  ) => {
    try {
      const sub = await readProductSub(merchantId, "ebuosystem");
      if (!sub) return rejectWithValue("Merchant not found");

      const args = {
        tier: (sub.tier as SubscriptionTier) || "basic",
        discountApplied: discountApplied ?? sub.discountApplied ?? {},
        creditsAvailable: creditsAvailable ?? sub.creditsAvailable ?? 0,
        vatRate: vatRate ?? sub.vatRate ?? 0,
        customTerms: !!sub.customTerms,
      };

      return {
        ...computeEbuosSubscription({ ...args, cadence: cadenceOverride || sub.cadence || "monthly" }),
        merchantId,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveEbuosSubscription = createAsyncThunk(
  "subscription/saveEbuos",
  async (
    { merchantId, tier = "basic", cadence = "monthly", vatRate = 0, customTerms = false }:
    { merchantId: string; tier?: SubscriptionTier; cadence?: TierType; vatRate?: number; customTerms?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeEbuosSubscription({ tier, cadence, vatRate, customTerms });
      if (priced.contactSales || !("recurringCharge" in priced)) return rejectWithValue(SALES_BLOCK);

      await persistProductSub(merchantId, "ebuosystem", {
        tier: priced.tier,
        cadence: priced.cadence,
        vatRate,
        customTerms,
        monthlyBase: priced.monthlyBase,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Thunks — digital creator ───────────────────────────
export const calculateDigitalCreatorSubscription = createAsyncThunk(
  "subscription/calculateDigitalCreator",
  async (args: Parameters<typeof computeDigitalCreatorSubscription>[0]) => ({
    ...computeDigitalCreatorSubscription(args),
    priceByTier: priceEveryDigitalCreatorTier(args),
  })
);

export const fetchAndPriceDigitalCreatorSubscription = createAsyncThunk(
  "subscription/fetchAndPriceDigitalCreator",
  async (
    { merchantId, tierOverride, cadenceOverride, channelsByPlatformOverride, vatRate, creditsAvailable, discountApplied }:
    { merchantId: string; tierOverride?: SubscriptionTier; cadenceOverride?: TierType; channelsByPlatformOverride?: Record<number, number>; vatRate?: number; creditsAvailable?: number; discountApplied?: DiscountFlags },
    { rejectWithValue }
  ) => {
    try {
      const sub = await readProductSub(merchantId, "digitalCreator");
      if (!sub) return rejectWithValue("Merchant not found");

      const args = {
        channelsByPlatform: channelsByPlatformOverride ?? (sub.channelsByPlatform as Record<number, number>) ?? { 1: 1 },
        discountApplied: discountApplied ?? sub.discountApplied ?? {},
        creditsAvailable: creditsAvailable ?? sub.creditsAvailable ?? 0,
        vatRate: vatRate ?? sub.vatRate ?? 0,
        customTerms: !!sub.customTerms,
      };

      return {
        ...computeDigitalCreatorSubscription({ ...args, tier: tierOverride || sub.tier, cadence: cadenceOverride || sub.cadence || "monthly" }),
        merchantId,
        priceByTier: priceEveryDigitalCreatorTier(args),
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveDigitalCreatorSubscription = createAsyncThunk(
  "subscription/saveDigitalCreator",
  async (
    { merchantId, tier, channelsByPlatform = { 1: 1 }, cadence = "monthly", vatRate = 0, customTerms = false }:
    { merchantId: string; tier?: SubscriptionTier; channelsByPlatform?: Record<number, number>; cadence?: TierType; vatRate?: number; customTerms?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeDigitalCreatorSubscription({ tier, channelsByPlatform, cadence, vatRate, customTerms });
      if (priced.contactSales || !("recurringCharge" in priced)) return rejectWithValue(SALES_BLOCK);

      await persistProductSub(merchantId, "digitalCreator", {
        tier: priced.tier,
        connectedChannels: priced.connectedChannels,
        channelsByPlatform: priced.channelsByPlatform,
        cadence: priced.cadence,
        vatRate,
        customTerms,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Thunks — automation ────────────────────────────────
export const calculateAutomationSubscription = createAsyncThunk(
  "subscription/calculateAutomation",
  async (args: Parameters<typeof computeAutomationSubscription>[0]) => ({
    ...computeAutomationSubscription(args),
    priceByTier: priceEveryAutomationTier(args),
    recommendedTier: recommendAutomationTier(args),
  })
);

export const fetchAndPriceAutomationSubscription = createAsyncThunk(
  "subscription/fetchAndPriceAutomation",
  async (
    { merchantId, tierOverride, cadenceOverride, tasksOverride, workflowsOverride, vatRate, creditsAvailable, discountApplied }:
    { merchantId: string; tierOverride?: SubscriptionTier; cadenceOverride?: TierType; tasksOverride?: number; workflowsOverride?: number; vatRate?: number; creditsAvailable?: number; discountApplied?: DiscountFlags },
    { rejectWithValue }
  ) => {
    try {
      const sub = await readProductSub(merchantId, "automation");
      if (!sub) return rejectWithValue("Merchant not found");

      const args = {
        tasksPerMonth: tasksOverride ?? sub.tasksPerMonth ?? 0,
        activeWorkflows: workflowsOverride ?? sub.activeWorkflows ?? 1,
        moduleIds: (sub.moduleIds as number[]) || [],
        discountApplied: discountApplied ?? sub.discountApplied ?? {},
        creditsAvailable: creditsAvailable ?? sub.creditsAvailable ?? 0,
        vatRate: vatRate ?? sub.vatRate ?? 0,
        customTerms: !!sub.customTerms,
      };

      return {
        ...computeAutomationSubscription({ ...args, tier: tierOverride || sub.tier, cadence: cadenceOverride || sub.cadence || "monthly" }),
        merchantId,
        priceByTier: priceEveryAutomationTier(args),
        recommendedTier: recommendAutomationTier(args),
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveAutomationSubscription = createAsyncThunk(
  "subscription/saveAutomation",
  async (
    { merchantId, tier, tasksPerMonth = 0, activeWorkflows = 1, moduleIds = [], cadence = "monthly", vatRate = 0, customTerms = false }:
    { merchantId: string; tier?: SubscriptionTier; tasksPerMonth?: number; activeWorkflows?: number; moduleIds?: number[]; cadence?: TierType; vatRate?: number; customTerms?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeAutomationSubscription({ tier, tasksPerMonth, activeWorkflows, moduleIds, cadence, vatRate, customTerms });
      if (priced.contactSales || !("recurringCharge" in priced)) return rejectWithValue(SALES_BLOCK);

      await persistProductSub(merchantId, "automation", {
        tier: priced.tier,
        tasksPerMonth: priced.tasksPerMonth,
        activeWorkflows: priced.activeWorkflows,
        moduleIds: priced.activeModules,
        cadence: priced.cadence,
        vatRate,
        customTerms,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Thunks — customer agent ────────────────────────────
export const calculateCustomerAgentSubscription = createAsyncThunk(
  "subscription/calculateCustomerAgent",
  async (args: Parameters<typeof computeCustomerAgentSubscription>[0]) => ({
    ...computeCustomerAgentSubscription(args),
    priceByTier: priceEveryCustomerAgentTier(args),
    recommendedTier: recommendCustomerAgentTier(args),
  })
);

export const fetchAndPriceCustomerAgentSubscription = createAsyncThunk(
  "subscription/fetchAndPriceCustomerAgent",
  async (
    { merchantId, tierOverride, cadenceOverride, conversationsOverride, voiceMinutesOverride, seatsOverride, vatRate, creditsAvailable, discountApplied }:
    { merchantId: string; tierOverride?: SubscriptionTier; cadenceOverride?: TierType; conversationsOverride?: number; voiceMinutesOverride?: number; seatsOverride?: number; vatRate?: number; creditsAvailable?: number; discountApplied?: DiscountFlags },
    { rejectWithValue }
  ) => {
    try {
      const sub = await readProductSub(merchantId, "customerAgent");
      if (!sub) return rejectWithValue("Merchant not found");

      const args = {
        conversationsPerMonth: conversationsOverride ?? sub.conversationsPerMonth ?? 0,
        voiceMinutes: voiceMinutesOverride ?? sub.voiceMinutes ?? 0,
        seats: seatsOverride ?? sub.seats ?? 1,
        discountApplied: discountApplied ?? sub.discountApplied ?? {},
        creditsAvailable: creditsAvailable ?? sub.creditsAvailable ?? 0,
        vatRate: vatRate ?? sub.vatRate ?? 0,
        customTerms: !!sub.customTerms,
      };

      return {
        ...computeCustomerAgentSubscription({ ...args, tier: tierOverride || sub.tier, cadence: cadenceOverride || sub.cadence || "monthly" }),
        merchantId,
        priceByTier: priceEveryCustomerAgentTier(args),
        recommendedTier: recommendCustomerAgentTier(args),
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveCustomerAgentSubscription = createAsyncThunk(
  "subscription/saveCustomerAgent",
  async (
    { merchantId, tier, conversationsPerMonth = 0, voiceMinutes = 0, seats = 1, cadence = "monthly", vatRate = 0, customTerms = false }:
    { merchantId: string; tier?: SubscriptionTier; conversationsPerMonth?: number; voiceMinutes?: number; seats?: number; cadence?: TierType; vatRate?: number; customTerms?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeCustomerAgentSubscription({ tier, conversationsPerMonth, voiceMinutes, seats, cadence, vatRate, customTerms });
      if (priced.contactSales || !("recurringCharge" in priced)) return rejectWithValue(SALES_BLOCK);

      await persistProductSub(merchantId, "customerAgent", {
        tier: priced.tier,
        conversationsPerMonth: priced.conversationsPerMonth,
        voiceMinutes: priced.voiceMinutes,
        seats: priced.seats,
        cadence: priced.cadence,
        vatRate,
        customTerms,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Thunks — security ──────────────────────────────────
export const calculateSecuritySubscription = createAsyncThunk(
  "subscription/calculateSecurity",
  async (args: Parameters<typeof computeSecuritySubscription>[0]) => ({
    ...computeSecuritySubscription(args),
    priceByTier: priceEverySecurityTier(args),
    recommendedTier: recommendSecurityTier(args),
  })
);

export const fetchAndPriceSecuritySubscription = createAsyncThunk(
  "subscription/fetchAndPriceSecurity",
  async (
    { merchantId, tierOverride, cadenceOverride, endpointsOverride, backupGbOverride, vatRate, creditsAvailable, discountApplied }:
    { merchantId: string; tierOverride?: SubscriptionTier; cadenceOverride?: TierType; endpointsOverride?: number; backupGbOverride?: number; vatRate?: number; creditsAvailable?: number; discountApplied?: DiscountFlags },
    { rejectWithValue }
  ) => {
    try {
      const sub = await readProductSub(merchantId, "security");
      if (!sub) return rejectWithValue("Merchant not found");

      const args = {
        endpoints: endpointsOverride ?? sub.endpoints ?? 1,
        backupGb: backupGbOverride ?? sub.backupGb ?? 0,
        controlIds: (sub.controlIds as number[]) || [],
        discountApplied: discountApplied ?? sub.discountApplied ?? {},
        creditsAvailable: creditsAvailable ?? sub.creditsAvailable ?? 0,
        vatRate: vatRate ?? sub.vatRate ?? 0,
        customTerms: !!sub.customTerms,
      };

      return {
        ...computeSecuritySubscription({ ...args, tier: tierOverride || sub.tier, cadence: cadenceOverride || sub.cadence || "monthly" }),
        merchantId,
        priceByTier: priceEverySecurityTier(args),
        recommendedTier: recommendSecurityTier(args),
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveSecuritySubscription = createAsyncThunk(
  "subscription/saveSecurity",
  async (
    { merchantId, tier, endpoints = 1, backupGb = 0, controlIds = [], cadence = "monthly", vatRate = 0, customTerms = false }:
    { merchantId: string; tier?: SubscriptionTier; endpoints?: number; backupGb?: number; controlIds?: number[]; cadence?: TierType; vatRate?: number; customTerms?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeSecuritySubscription({ tier, endpoints, backupGb, controlIds, cadence, vatRate, customTerms });
      if (priced.contactSales || !("recurringCharge" in priced)) return rejectWithValue(SALES_BLOCK);

      await persistProductSub(merchantId, "security", {
        tier: priced.tier,
        endpoints: priced.endpoints,
        backupGb: priced.backupGb,
        controlIds: priced.activeControls,
        cadence: priced.cadence,
        vatRate,
        customTerms,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Thunks — website ───────────────────────────────────
export const saveWebsiteSelection = createAsyncThunk(
  "subscription/saveWebsite",
  async (
    { merchantId, typeId, cadence = "monthly", vatRate = 0 }:
    { merchantId: string; typeId: number; cadence?: TierType; vatRate?: number },
    { rejectWithValue }
  ) => {
    try {
      const priced = computeWebsiteSubscription({ typeId, cadence, vatRate });
      if (!priced) return rejectWithValue("Unknown website type");

      await persistProductSub(merchantId, "website", {
        typeId: priced.typeId,
        cadence,
        vatRate,
        setupFee: priced.setupFee,
        monthlyHosting: priced.monthlyHosting,
        lastQuoted: priced.totalCharged,
      });
      return { ...priced, merchantId };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────
const meteredDefaults = {
  tier: "starter" as SubscriptionTier,
  tierName: "Starter",
  tierWasLifted: false,
  contactSales: false,
  contactSalesReasons: [] as string[],
  contactUrl: ENTERPRISE.contactUrl,

  cadence: "monthly" as TierType,
  cadenceLabel: "Monthly",
  cadenceShort: "/month",
  monthEquivalent: 0,
  savingsVsMonthly: 0,

  requiresUpgradeTo: null as SubscriptionTier | null,
  platformBase: 0,
  monthlyTotal: 0,
  recurringCharge: 0,
  discountPercent: 0,
  promoDiscount: 0,
  creditsAvailable: 0,
  creditsUsed: 0,
  vatRate: 0,
  vat: 0,
  totalCharged: 0 as number | null,

  loading: false,
  saving: false,
  error: null as string | null,
};

const initialDigitalCreatorState = {
  ...meteredDefaults,
  connectedChannels: 1,
  channelsByPlatform: { 1: 1 } as Record<number, number>,
  includedChannels: 0,
  extraChannels: 0,
  channelCharge: 0,
  platformTypeIds: [1] as number[],
  includedPlatformTypes: 0,
  extraPlatformTypes: 0,
  extraPlatformTypeMonthly: 0,
  platformTypeCharge: 0,
  perPlatformBreakdown: [] as { platformId: number; label: string; channels: number; postsAllocated: number; charge: number }[],
  postsPerDay: 0,
  postsIncluded: 0,
  pricePerPost: 0,
  contentCharge: 0,
  priceByTier: {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; postsIncluded: number }>,
};

const initialAutomationState = {
  ...meteredDefaults,
  tasksPerMonth: 0,
  includedTasks: 0,
  billableTasks: 0,
  taskCharge: 0,
  effectiveCostPerTask: 0,
  taskBreakdown: [] as ScaleLine[],
  activeWorkflows: 0,
  workflowAllowance: 0,
  workflowCapReached: false,
  requestedModules: [] as number[],
  activeModules: [] as number[],
  blockedModules: [] as number[],
  priceByTier: {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; blocked: number }>,
  recommendedTier: "starter" as SubscriptionTier,
};

const initialCustomerAgentState = {
  ...meteredDefaults,
  conversationsPerMonth: 0,
  includedConversations: 0,
  billableConversations: 0,
  conversationCharge: 0,
  effectiveCostPerConversation: 0,
  conversationBreakdown: [] as ScaleLine[],
  voiceEnabled: false,
  voiceMinutes: 0,
  voiceCharge: 0,
  voiceBreakdown: [] as ScaleLine[],
  seats: 1,
  includedSeats: 1,
  extraSeats: 0,
  seatCharge: 0,
  priceByTier: {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; costPerConversation: number }>,
  recommendedTier: "starter" as SubscriptionTier,
};

const initialSecurityState = {
  ...meteredDefaults,
  endpoints: 1,
  includedEndpoints: 0,
  billableEndpoints: 0,
  endpointCharge: 0,
  effectiveCostPerEndpoint: 0,
  endpointBreakdown: [] as ScaleLine[],
  backupsEnabled: false,
  backupGb: 0,
  includedBackupGb: 0,
  backupCharge: 0,
  backupBreakdown: [] as ScaleLine[],
  requestedControls: [] as number[],
  activeControls: [] as number[],
  blockedControls: [] as number[],
  monitoring: "none" as string,
  priceByTier: {} as Record<SubscriptionTier, { totalCharged: number | null; recurringCharge: number; blocked: number }>,
  recommendedTier: "starter" as SubscriptionTier,
};

const initialWebsiteState = {
  typeId: null as number | null,
  typeName: null as string | null,
  complexity: null as string | null,
  cadence: "monthly" as TierType,
  setupFee: 0,
  monthlyHosting: 0,
  hostingCharge: 0,
  totalCharged: 0 as number | null,
  saving: false,
  error: null as string | null,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: {
    merchantId: null as string | null,

    /* Which product is currently selected/active in the UI. Doesn't
       gate anything — every product below can be priced
       independently of this. */
    activeProduct: "ebuosystem" as ProductType,

    tier: "basic" as SubscriptionTier,
    tierName: "Basic",
    contactSales: false,
    contactSalesReasons: [] as string[],
    contactUrl: ENTERPRISE.contactUrl,

    cadence: "monthly" as TierType,
    cadenceLabel: "Monthly",
    cadenceShort: "/month",
    monthEquivalent: 0,
    savingsVsMonthly: 0,

    activeFeatures: [] as number[],
    handledElsewhere: [] as number[],
    includedSubcategoryTotal: 0,
    totalSubcategoryTotal: 0,
    listMonthlyTotal: 0,
    bundleDiscountPercent: 0,
    perCategory: [] as { categoryId: number; categoryName: string; tierName: string; includedSubcategories: number; totalSubcategories: number; monthlyTotal: number }[],

    monthlyBase: 0,
    recurringCharge: 0,
    discountPercent: 0,
    promoDiscount: 0,
    creditsAvailable: 0,
    creditsUsed: 0,
    vatRate: 0,
    vat: 0,
    totalCharged: 0 as number | null,

    loading: false,
    saving: false,
    error: null as string | null,

    digitalCreator: { ...initialDigitalCreatorState },
    automation: { ...initialAutomationState },
    customerAgent: { ...initialCustomerAgentState },
    security: { ...initialSecurityState },
    website: { ...initialWebsiteState },
  },
  reducers: {
    resetSubscription: (state) => {
      Object.assign(state, subscriptionSlice.getInitialState());
    },
    setActiveProduct: (state, action: { payload: ProductType }) => {
      state.activeProduct = action.payload;
    },
    preview: (state, action) => {
      const { cadence, tier } = action.payload || {};
      const args = {
        cadence: cadence ?? state.cadence,
        tier: tier ?? state.tier,
        creditsAvailable: state.creditsAvailable,
        vatRate: state.vatRate,
      };
      Object.assign(state, computeEbuosSubscription(args), { merchantId: state.merchantId });
    },
    previewDigitalCreator: (state, action) => {
      const { tier, cadence, channelsByPlatform } = action.payload || {};
      const args = {
        tier: tier ?? state.digitalCreator.tier,
        cadence: cadence ?? state.digitalCreator.cadence,
        channelsByPlatform: channelsByPlatform ?? state.digitalCreator.channelsByPlatform,
        creditsAvailable: state.digitalCreator.creditsAvailable,
        vatRate: state.digitalCreator.vatRate,
      };
      Object.assign(state.digitalCreator, computeDigitalCreatorSubscription(args), {
        priceByTier: priceEveryDigitalCreatorTier(args),
      });
    },
    previewAutomation: (state, action) => {
      const { tier, cadence, tasksPerMonth, activeWorkflows, moduleIds } = action.payload || {};
      const args = {
        tier: tier ?? state.automation.tier,
        cadence: cadence ?? state.automation.cadence,
        tasksPerMonth: tasksPerMonth ?? state.automation.tasksPerMonth,
        activeWorkflows: activeWorkflows ?? state.automation.activeWorkflows,
        moduleIds: moduleIds ?? state.automation.requestedModules,
        creditsAvailable: state.automation.creditsAvailable,
        vatRate: state.automation.vatRate,
      };
      Object.assign(state.automation, computeAutomationSubscription(args), {
        priceByTier: priceEveryAutomationTier(args),
        recommendedTier: recommendAutomationTier(args),
      });
    },
    previewCustomerAgent: (state, action) => {
      const { tier, cadence, conversationsPerMonth, voiceMinutes, seats } = action.payload || {};
      const args = {
        tier: tier ?? state.customerAgent.tier,
        cadence: cadence ?? state.customerAgent.cadence,
        conversationsPerMonth: conversationsPerMonth ?? state.customerAgent.conversationsPerMonth,
        voiceMinutes: voiceMinutes ?? state.customerAgent.voiceMinutes,
        seats: seats ?? state.customerAgent.seats,
        creditsAvailable: state.customerAgent.creditsAvailable,
        vatRate: state.customerAgent.vatRate,
      };
      Object.assign(state.customerAgent, computeCustomerAgentSubscription(args), {
        priceByTier: priceEveryCustomerAgentTier(args),
        recommendedTier: recommendCustomerAgentTier(args),
      });
    },
    previewSecurity: (state, action) => {
      const { tier, cadence, endpoints, backupGb, controlIds } = action.payload || {};
      const args = {
        tier: tier ?? state.security.tier,
        cadence: cadence ?? state.security.cadence,
        endpoints: endpoints ?? state.security.endpoints,
        backupGb: backupGb ?? state.security.backupGb,
        controlIds: controlIds ?? state.security.requestedControls,
        creditsAvailable: state.security.creditsAvailable,
        vatRate: state.security.vatRate,
      };
      Object.assign(state.security, computeSecuritySubscription(args), {
        priceByTier: priceEverySecurityTier(args),
        recommendedTier: recommendSecurityTier(args),
      });
    },
    previewWebsite: (state, action) => {
      const { typeId, cadence } = action.payload || {};
      const priced = computeWebsiteSubscription({
        typeId: typeId ?? state.website.typeId ?? 301,
        cadence: cadence ?? state.website.cadence,
      });
      if (priced) Object.assign(state.website, priced);
    },
    resetDigitalCreator: (state) => { state.digitalCreator = { ...initialDigitalCreatorState }; },
    resetAutomation: (state) => { state.automation = { ...initialAutomationState }; },
    resetCustomerAgent: (state) => { state.customerAgent = { ...initialCustomerAgentState }; },
    resetSecurity: (state) => { state.security = { ...initialSecurityState }; },
    resetWebsite: (state) => { state.website = { ...initialWebsiteState }; },
  },
  extraReducers: (builder) => {
    builder
      // ── ebuosystem ──
      .addCase(calculateEbuosSubscription.fulfilled, (state, action) => { Object.assign(state, action.payload); })
      .addCase(fetchAndPriceEbuosSubscription.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAndPriceEbuosSubscription.fulfilled, (state, action) => { Object.assign(state, action.payload); state.loading = false; })
      .addCase(fetchAndPriceEbuosSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Could not load your subscription.";
      })
      .addCase(saveEbuosSubscription.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(saveEbuosSubscription.fulfilled, (state, action) => { Object.assign(state, action.payload); state.saving = false; })
      .addCase(saveEbuosSubscription.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Could not save your subscription.";
      })

      // ── digital creator ──
      .addCase(calculateDigitalCreatorSubscription.fulfilled, (state, action) => { Object.assign(state.digitalCreator, action.payload); })
      .addCase(fetchAndPriceDigitalCreatorSubscription.pending, (state) => { state.digitalCreator.loading = true; state.digitalCreator.error = null; })
      .addCase(fetchAndPriceDigitalCreatorSubscription.fulfilled, (state, action) => { Object.assign(state.digitalCreator, action.payload); state.digitalCreator.loading = false; })
      .addCase(fetchAndPriceDigitalCreatorSubscription.rejected, (state, action) => {
        state.digitalCreator.loading = false;
        state.digitalCreator.error = (action.payload as string) || "Could not load your Digital Creator plan.";
      })
      .addCase(saveDigitalCreatorSubscription.pending, (state) => { state.digitalCreator.saving = true; state.digitalCreator.error = null; })
      .addCase(saveDigitalCreatorSubscription.fulfilled, (state, action) => { Object.assign(state.digitalCreator, action.payload); state.digitalCreator.saving = false; })
      .addCase(saveDigitalCreatorSubscription.rejected, (state, action) => {
        state.digitalCreator.saving = false;
        state.digitalCreator.error = (action.payload as string) || "Could not save your Digital Creator plan.";
      })

      // ── automation ──
      .addCase(calculateAutomationSubscription.fulfilled, (state, action) => { Object.assign(state.automation, action.payload); })
      .addCase(fetchAndPriceAutomationSubscription.pending, (state) => { state.automation.loading = true; state.automation.error = null; })
      .addCase(fetchAndPriceAutomationSubscription.fulfilled, (state, action) => { Object.assign(state.automation, action.payload); state.automation.loading = false; })
      .addCase(fetchAndPriceAutomationSubscription.rejected, (state, action) => {
        state.automation.loading = false;
        state.automation.error = (action.payload as string) || "Could not load your automation plan.";
      })
      .addCase(saveAutomationSubscription.pending, (state) => { state.automation.saving = true; state.automation.error = null; })
      .addCase(saveAutomationSubscription.fulfilled, (state, action) => { Object.assign(state.automation, action.payload); state.automation.saving = false; })
      .addCase(saveAutomationSubscription.rejected, (state, action) => {
        state.automation.saving = false;
        state.automation.error = (action.payload as string) || "Could not save your automation plan.";
      })

      // ── customer agent ──
      .addCase(calculateCustomerAgentSubscription.fulfilled, (state, action) => { Object.assign(state.customerAgent, action.payload); })
      .addCase(fetchAndPriceCustomerAgentSubscription.pending, (state) => { state.customerAgent.loading = true; state.customerAgent.error = null; })
      .addCase(fetchAndPriceCustomerAgentSubscription.fulfilled, (state, action) => { Object.assign(state.customerAgent, action.payload); state.customerAgent.loading = false; })
      .addCase(fetchAndPriceCustomerAgentSubscription.rejected, (state, action) => {
        state.customerAgent.loading = false;
        state.customerAgent.error = (action.payload as string) || "Could not load your AI agent plan.";
      })
      .addCase(saveCustomerAgentSubscription.pending, (state) => { state.customerAgent.saving = true; state.customerAgent.error = null; })
      .addCase(saveCustomerAgentSubscription.fulfilled, (state, action) => { Object.assign(state.customerAgent, action.payload); state.customerAgent.saving = false; })
      .addCase(saveCustomerAgentSubscription.rejected, (state, action) => {
        state.customerAgent.saving = false;
        state.customerAgent.error = (action.payload as string) || "Could not save your AI agent plan.";
      })

      // ── security ──
      .addCase(calculateSecuritySubscription.fulfilled, (state, action) => { Object.assign(state.security, action.payload); })
      .addCase(fetchAndPriceSecuritySubscription.pending, (state) => { state.security.loading = true; state.security.error = null; })
      .addCase(fetchAndPriceSecuritySubscription.fulfilled, (state, action) => { Object.assign(state.security, action.payload); state.security.loading = false; })
      .addCase(fetchAndPriceSecuritySubscription.rejected, (state, action) => {
        state.security.loading = false;
        state.security.error = (action.payload as string) || "Could not load your security plan.";
      })
      .addCase(saveSecuritySubscription.pending, (state) => { state.security.saving = true; state.security.error = null; })
      .addCase(saveSecuritySubscription.fulfilled, (state, action) => { Object.assign(state.security, action.payload); state.security.saving = false; })
      .addCase(saveSecuritySubscription.rejected, (state, action) => {
        state.security.saving = false;
        state.security.error = (action.payload as string) || "Could not save your security plan.";
      })

      // ── website ──
      .addCase(saveWebsiteSelection.pending, (state) => { state.website.saving = true; state.website.error = null; })
      .addCase(saveWebsiteSelection.fulfilled, (state, action) => { Object.assign(state.website, action.payload); state.website.saving = false; })
      .addCase(saveWebsiteSelection.rejected, (state, action) => {
        state.website.saving = false;
        state.website.error = (action.payload as string) || "Could not save your website selection.";
      });
  },
});

export const {
  resetSubscription,
  setActiveProduct,
  preview,
  previewDigitalCreator,
  previewAutomation,
  previewCustomerAgent,
  previewSecurity,
  previewWebsite,
  resetDigitalCreator,
  resetAutomation,
  resetCustomerAgent,
  resetSecurity,
  resetWebsite,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;